import { supabase } from "@/integrations/supabase/client";

export interface ProfileCompletionData {
  nickname: string;
  preferredGame: string;
  role: 'mentor' | 'jogador';
  whatsapp_number?: string;
  bio?: string;
}

export interface ProfileCompletionResult {
  success: boolean;
  error?: string;
  data?: ProfileCompletionData;
}

/**
 * Verifica se o perfil está completo para fazer compras
 * ATUALIZADO: Só mostra modal para usuários Google OAuth recentes
 */
export const isProfileComplete = async (userId: string): Promise<boolean> => {
  try {
    // Primeiro, buscar dados do usuário autenticado para detectar se é Google OAuth
    const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !authUser) {
      console.error('❌ [profileCompletionService] Erro ao obter usuário autenticado:', authError);
      return true; // Se não conseguir verificar, assume que está completo para evitar loops
    }

    // Detectar se é usuário Google OAuth
    const isGoogleUser = authUser.app_metadata?.providers?.includes('google') || 
                        authUser.user_metadata?.iss?.includes('accounts.google.com');

    console.log('🔍 [profileCompletionService] Verificando usuário:', {
      userId,
      email: authUser.email,
      isGoogleUser,
      providers: authUser.app_metadata?.providers,
      userMetadata: authUser.user_metadata
    });

    // Buscar dados do perfil
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('nickname, preferred_game, role, role_selected_at, created_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ [profileCompletionService] Erro ao verificar perfil:', error);
      return true; // Se não conseguir verificar, assume que está completo
    }

    // Calcular tempo desde a criação do perfil
    const profileCreatedAt = new Date(profile.created_at);
    const now = new Date();
    const timeDiffMinutes = (now.getTime() - profileCreatedAt.getTime()) / (1000 * 60);

    console.log('📊 [profileCompletionService] Análise do perfil:', {
      userId,
      nickname: profile.nickname,
      preferredGame: profile.preferred_game,
      role: profile.role,
      roleSelectedAt: profile.role_selected_at,
      createdAt: profile.created_at,
      timeDiffMinutes: Math.round(timeDiffMinutes),
      isGoogleUser
    });

    // LÓGICA ATUALIZADA:
    // 1. Se NÃO é usuário Google OAuth → sempre considera completo (usuários antigos)
    if (!isGoogleUser) {
      console.log('✅ [profileCompletionService] Usuário tradicional - perfil considerado completo');
      return true;
    }

    // 2. Se É usuário Google OAuth, verificar se precisa completar:
    // - Perfil criado nos últimos 30 minutos (tempo suficiente para completar)
    // - Não tem role_selected_at (não completou o modal ainda)
    const isRecentProfile = timeDiffMinutes < 30;
    const hasRoleSelectedAt = profile.role_selected_at !== null;

    if (isGoogleUser && isRecentProfile && !hasRoleSelectedAt) {
      console.log('⚠️ [profileCompletionService] Usuário Google OAuth recente sem role_selected_at - precisa completar');
      return false; // Mostra modal
    }

    console.log('✅ [profileCompletionService] Usuário Google OAuth - perfil considerado completo');
    return true;

  } catch (error) {
    console.error('❌ [profileCompletionService] Erro na verificação:', error);
    return true; // Em caso de erro, assume que está completo para evitar loops
  }
};

/**
 * Completa o perfil do usuário com os dados fornecidos
 */
export const completeProfile = async (
  userId: string,
  data: ProfileCompletionData
): Promise<ProfileCompletionResult> => {
  try {
    console.log('🔄 [profileCompletionService] Iniciando completamento de perfil:', {
      userId,
      data: { ...data, whatsapp_number: data.whatsapp_number ? '[HIDDEN]' : undefined }
    });

    // Validar dados obrigatórios
    if (!data.nickname || data.nickname.trim().length < 2) {
      return {
        success: false,
        error: 'Nome deve ter pelo menos 2 caracteres'
      };
    }

    if (!data.preferredGame) {
      return {
        success: false,
        error: 'Jogo preferido é obrigatório'
      };
    }

    if (!data.role || !['mentor', 'jogador'].includes(data.role)) {
      return {
        success: false,
        error: 'Role deve ser mentor ou jogador'
      };
    }

    // Verificar se nickname já existe
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('nickname', data.nickname.trim())
      .neq('id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('❌ [profileCompletionService] Erro ao verificar nickname:', checkError);
      return {
        success: false,
        error: 'Erro ao verificar disponibilidade do nome'
      };
    }

    if (existingProfile) {
      return {
        success: false,
        error: 'Este nome já está em uso'
      };
    }

    // Atualizar perfil
    const updateData = {
      nickname: data.nickname.trim(),
      preferred_game: data.preferredGame,
      role: data.role,
      role_selected_at: new Date().toISOString(),
      whatsapp_number: data.whatsapp_number?.trim() || null,
      bio: data.bio?.trim() || null,
      updated_at: new Date().toISOString()
    };

    const { data: updatedProfile, error: updateError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (updateError) {
      console.error('❌ [profileCompletionService] Erro ao atualizar perfil:', updateError);
      return {
        success: false,
        error: 'Erro ao salvar perfil'
      };
    }

    console.log('✅ [profileCompletionService] Perfil completado com sucesso:', {
      userId,
      nickname: updatedProfile.nickname,
      role: updatedProfile.role,
      roleSelectedAt: updatedProfile.role_selected_at
    });

    return {
      success: true,
      data: updatedProfile
    };

  } catch (error) {
    console.error('❌ [profileCompletionService] Erro no completamento:', error);
    return {
      success: false,
      error: 'Erro interno do servidor'
    };
  }
};

/**
 * Busca dados atuais do perfil
 */
export const getProfileData = async (userId: string) => {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('nickname, preferred_game, role, whatsapp_number, bio, role_selected_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ [profileCompletionService] Erro ao buscar perfil:', error);
      return null;
    }

    return profile;
  } catch (error) {
    console.error('❌ [profileCompletionService] Erro na busca do perfil:', error);
    return null;
  }
};

/**
 * Lista de jogos disponíveis
 */
export const getAvailableGames = () => [
  { value: 'League of Legends', label: 'League of Legends' },
  { value: 'Valorant', label: 'Valorant' },
  { value: 'Fortnite', label: 'Fortnite' },
  { value: 'Pokemon TCG', label: 'Pokemon TCG' },
  { value: 'CS2', label: 'Counter-Strike 2' },
  { value: 'Apex Legends', label: 'Apex Legends' },
  { value: 'Rocket League', label: 'Rocket League' }
]; 